/**
 * DIEUKYDIEU.TV - AUDIO & MEDIA ENGINE (MODULAR CORE TRANSPORT)
 * Mini Player, Full Player, Bottom Tabs, Sleep Timer, Volume & Gestures.
 */

import { t } from './i18n.js';
import { showToast } from './ui.js';
import { audioPlaylist } from './data/player-playlist.js';
import {
    currentAudioIndex,
    isShuffle,
    repeatMode,
    currentPlaybackSpeed,
    favoriteTracks,
    setCurrentAudioIndex,
    setIsShuffle,
    setRepeatMode
} from './modules/player-state.js';
import {
    getTrackTitle,
    getTrackArtist,
    updatePlayState
} from './modules/player-dom.js';
import {
    renderSyncedLyrics
} from './modules/player-lyrics.js';
import {
    isSleepOnEnd,
    setSleepTimer,
    updateFavoriteBtn
} from './modules/player-controls.js';
import {
    renderQueueList
} from './modules/player-queue.js';

// Re-export all sub-module APIs to maintain 100% backward compatibility
export { audioPlaylist } from './data/player-playlist.js';
export {
    currentAudioIndex,
    isShuffle,
    repeatMode,
    currentPlaybackSpeed,
    isRemainingTimeMode,
    currentLyricIndex,
    favoriteTracks,
    activePlayerTab
} from './modules/player-state.js';
export {
    getTrackTitle,
    getTrackArtist,
    updateCurrentTrackDisplay,
    updatePlayState,
    updateSliderTrack
} from './modules/player-dom.js';
export {
    formatTime,
    formatTimeRemaining,
    toggleTimeDisplayMode,
    getTrackSyncedLyrics,
    renderSyncedLyrics,
    seekToLyricTime,
    updateLyricsHighlight
} from './modules/player-lyrics.js';
export {
    setVolume,
    initVolumeControl,
    updateVolumeIcon,
    toggleMute,
    openSleepTimerModal,
    closeSleepTimerModal,
    setSleepTimer,
    openSleepTimerFromMenu,
    toggleOptionsMenu,
    closeOptionsMenu,
    toggleFavoriteCurrentTrack,
    updateFavoriteBtn,
    shareCurrentTrack
} from './modules/player-controls.js';
export {
    switchPlayerTab,
    toggleLyricsTab,
    toggleQueueTab,
    renderQueueList,
    shufflePlaylist,
    cyclePlaybackSpeed
} from './modules/player-queue.js';
export {
    openFullPlayer,
    closeFullPlayer,
    closePlayer,
    initSeekbar,
    initAudioElementEvents,
    initKeyboardShortcuts
} from './modules/player-ui.js';
export {
    initPlayerSwipeGestures,
    initMiniPlayerSwipeDismiss
} from './modules/player-gestures.js';

export function playAudio(index) {
    if (index < 0 || index >= audioPlaylist.length) return;
    setCurrentAudioIndex(index);
    const track = audioPlaylist[index];
    const audioEl = document.getElementById('main-audio');
    const miniPlayer = document.getElementById('mini-player');
    if (!track || !audioEl) return;

    audioEl.src = track.src;
    audioEl.playbackRate = currentPlaybackSpeed;

    const trackTitle = getTrackTitle(track);
    const trackArtist = getTrackArtist(track);

    // Mini Player
    const mpTitle = document.getElementById('mp-title');
    const mpArtist = document.getElementById('mp-artist');
    const mpThumb = document.getElementById('mp-thumb');
    if (mpTitle) mpTitle.innerText = trackTitle;
    if (mpArtist) mpArtist.innerText = trackArtist;
    if (mpThumb) mpThumb.style.backgroundImage = `url('${track.img}')`;

    // Full Player Ambient Backdrop & Artwork
    const fpAmbientBg = document.getElementById('fp-ambient-bg');
    const fpArt = document.getElementById('fp-art');
    if (fpAmbientBg) fpAmbientBg.style.backgroundImage = `url('${track.img}')`;
    if (fpArt) fpArt.style.backgroundImage = `url('${track.img}')`;

    // Full Player Titles
    const fpTitle = document.getElementById('fp-title');
    const fpArtist = document.getElementById('fp-artist');
    if (fpTitle) fpTitle.innerText = trackTitle;
    if (fpArtist) fpArtist.innerText = track.credits || trackArtist;

    // Lyrics View
    const fpLyricsTitle = document.getElementById('fp-lyrics-title');
    const fpLyricsArtist = document.getElementById('fp-lyrics-artist');
    if (fpLyricsTitle) fpLyricsTitle.innerText = trackTitle;
    if (fpLyricsArtist) fpLyricsArtist.innerText = track.credits || trackArtist;
    renderSyncedLyrics(track);

    // Favorite State
    const isFav = favoriteTracks.includes(track.title);
    updateFavoriteBtn(isFav);

    // Update Queue View UI
    renderQueueList();

    const playPromise = audioEl.play();
    if (playPromise !== undefined) {
        playPromise.catch(err => {
            console.warn('[Audio Player] Autoplay restricted or user interaction required:', err.message);
            updatePlayState(false);
        });
    }
    if (miniPlayer) miniPlayer.classList.add('active');
    document.body.classList.add('has-active-player');
    document.documentElement.style.setProperty('--player-height', 'var(--sp-16)');
    updatePlayState(true);
}

export function togglePlay(e) {
    if (e) e.stopPropagation();
    const audioEl = document.getElementById('main-audio');
    if (!audioEl) return;
    if (currentAudioIndex === -1 && audioPlaylist.length > 0) return playAudio(0);
    if (audioEl.paused) {
        const playPromise = audioEl.play();
        if (playPromise !== undefined) {
            playPromise.catch(err => {
                console.warn('[Audio Player] Playback blocked:', err.message);
                updatePlayState(false);
            });
        }
        updatePlayState(true);
    } else {
        audioEl.pause();
        updatePlayState(false);
    }
}

export function playNext(isAutoEnd = false) {
    const audioEl = document.getElementById('main-audio');
    if (isAutoEnd && isSleepOnEnd()) {
        setSleepTimer(0);
        if (audioEl) audioEl.pause();
        updatePlayState(false);
        showToast(t('toast_timer_stopped_end'));
        return;
    }

    if (repeatMode === 2 && isAutoEnd) {
        if (audioEl) {
            audioEl.currentTime = 0;
            audioEl.play().catch(e => console.warn(e));
            updatePlayState(true);
        }
        return;
    }

    if (isShuffle && audioPlaylist.length > 1) {
        let randIdx;
        do {
            randIdx = Math.floor(Math.random() * audioPlaylist.length);
        } while (randIdx === currentAudioIndex);
        playAudio(randIdx);
        return;
    }

    const nextIdx = currentAudioIndex + 1;
    if (nextIdx >= audioPlaylist.length) {
        if (repeatMode === 1) {
            playAudio(0);
        } else if (isAutoEnd) {
            if (audioEl) audioEl.pause();
            updatePlayState(false);
        } else {
            playAudio(0);
        }
    } else {
        playAudio(nextIdx);
    }
}

export function playPrev() {
    const audioEl = document.getElementById('main-audio');
    if (!audioEl) return;
    if (audioEl.currentTime > 3) {
        audioEl.currentTime = 0;
        return;
    }
    const prevIdx = (currentAudioIndex - 1 + audioPlaylist.length) % audioPlaylist.length;
    playAudio(prevIdx);
}

export function playNextBtn(e) {
    if (e) e.stopPropagation();
    playNext(false);
}

export function seekRelative(delta) {
    const audioEl = document.getElementById('main-audio');
    if (!audioEl || !audioEl.duration) return;
    audioEl.currentTime = Math.max(0, Math.min(audioEl.duration, audioEl.currentTime + delta));
}

export function toggleShuffle() {
    setIsShuffle(!isShuffle);
    const btn = document.getElementById('fp-shuffle-btn');
    if (btn) btn.classList.toggle('active', isShuffle);
    showToast(isShuffle ? t('toast_shuffle_on') : t('toast_shuffle_off'));
}

export function toggleRepeat() {
    setRepeatMode((repeatMode + 1) % 3);
    const btn = document.getElementById('fp-repeat-btn');
    if (!btn) return;

    if (repeatMode === 0) {
        btn.classList.remove('active');
        btn.innerHTML = `<span class="material-symbols-rounded">repeat</span>`;
        btn.title = "Lặp lại: Tắt";
        showToast(t('toast_repeat_off'));
    } else if (repeatMode === 1) {
        btn.classList.add('active');
        btn.innerHTML = `<span class="material-symbols-rounded">repeat</span>`;
        btn.title = "Lặp lại: Tất cả";
        showToast(t('toast_repeat_all'));
    } else if (repeatMode === 2) {
        btn.classList.add('active');
        btn.innerHTML = `<span class="material-symbols-rounded">repeat_one</span>`;
        btn.title = "Lặp lại: 1 bài";
        showToast(t('toast_repeat_one'));
    }
}
